(() => {
  'use strict';
  function initialize(root) {
    if (root.dataset.arInitialized) return;
    const viewer = root.querySelector('[data-scdwb-live-model]'), launch = root.querySelector('[data-scp-ar-launch]'), dialog = root.querySelector('[data-scp-ar-dialog]');
    if (!viewer || !launch || !dialog) return;
    root.dataset.arInitialized = 'true';
    const copy = JSON.parse(root.querySelector('[data-scp-ar-copy]').textContent);
    const status = dialog.querySelector('[data-scp-ar-status]'), prepare = dialog.querySelector('[data-scp-ar-prepare]'), open = dialog.querySelector('[data-scp-ar-open]');
    const help = dialog.querySelector('[data-scp-ar-device]');
    let revision = 0, busy = false, wantsAR = false, outputURL = '', modelURL = '', exportViewer, preparedState = '', controller;
    const probe = document.createElement('a');
    const supportsAR = Boolean(probe.relList?.supports?.('ar')) && typeof createImageBitmap === 'function' && typeof viewer.exportScene === 'function' && typeof viewer.prepareUSDZ === 'function';
    const mobile = navigator.userAgentData?.mobile === true || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const entry = root.querySelector('[data-scp-ar-entry]'), label = root.querySelector('[data-scp-ar-label]');
    let mode = '';
    help.hidden = supportsAR;
    open.hidden = true; prepare.hidden = true;
    function stateKey() { return root.dataset.wheelState || '{}'; }
    function isReady() { return Boolean(viewer.loaded && root.dataset.previewReadyState === stateKey()); }
    function release() {
      if (outputURL) URL.revokeObjectURL(outputURL);
      if (modelURL) URL.revokeObjectURL(modelURL);
      outputURL = ''; modelURL = ''; preparedState = '';
      exportViewer?.remove(); exportViewer = null;
      open.removeAttribute('href'); open.hidden = true;
    }
    function invalidate() {
      ++revision; controller?.abort(); controller = null; busy = false; release();
      prepare.disabled = !isReady(); prepare.hidden = true; prepare.textContent = copy.retry;
      dialog.removeAttribute('aria-busy');
      status.textContent = supportsAR ? (isReady() ? copy.intro : copy.loading) : '';
    }
    function loaded(element, signal) {
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => finish(new Error('AR model timed out')), 60000);
        const success = () => finish(); const failure = () => finish(new Error('AR model could not load'));
        const abort = () => finish(new DOMException('Superseded', 'AbortError'));
        function finish(error) { clearTimeout(timer); element.removeEventListener('load', success); element.removeEventListener('error', failure); signal.removeEventListener('abort', abort); error ? reject(error) : resolve(); }
        element.addEventListener('load', success, {once: true}); element.addEventListener('error', failure, {once: true}); signal.addEventListener('abort', abort, {once: true});
      });
    }
    async function generate() {
      if (busy || !isReady()) return;
      const request = ++revision, selectedState = stateKey();
      release(); busy = true; controller = new AbortController(); const signal = controller.signal;
      prepare.disabled = true; dialog.setAttribute('aria-busy', 'true'); status.textContent = copy.preparing;
      const assertCurrent = () => { if (signal.aborted || request !== revision || selectedState !== stateKey()) throw new DOMException('Superseded', 'AbortError'); };
      try {
        const module = await import(root.querySelector('[data-scp-ar-copy]').dataset.exportModule);
        assertCurrent();
        const snapshot = await viewer.exportScene({binary: true, onlyVisible: true});
        assertCurrent();
        const result = await module.prepareWheelGLB(await snapshot.arrayBuffer(), {dimensions: viewer.getDimensions(), diameter: Number(root.querySelector('[data-scp-ar-copy]').dataset.diameter) / 1000, reverse: root.dataset.modelProfile === 'adro-lci'});
        assertCurrent();
        modelURL = URL.createObjectURL(result.blob);
        const secondary = document.createElement('model-viewer'); exportViewer = secondary;
        secondary.className = 'scp-ar-export-viewer'; secondary.setAttribute('aria-hidden', 'true'); secondary.setAttribute('tabindex', '-1');
        secondary.setAttribute('loading', 'eager'); secondary.setAttribute('reveal', 'auto'); secondary.setAttribute('ar-usdz-max-texture-size', '1024');
        const wait = loaded(secondary, signal); secondary.src = modelURL; root.appendChild(secondary); await wait;
        assertCurrent();
        if (typeof secondary.prepareUSDZ !== 'function') throw new Error('AR exporter unavailable');
        const generated = await secondary.prepareUSDZ();
        if (signal.aborted || request !== revision || selectedState !== stateKey()) { URL.revokeObjectURL(generated); throw new DOMException('Superseded', 'AbortError'); }
        secondary.remove(); exportViewer = null; URL.revokeObjectURL(modelURL); modelURL = '';
        const rawPackage = await fetch(generated).then(response => response.arrayBuffer()); URL.revokeObjectURL(generated);
        assertCurrent();
        const optimized = module.optimizeUSDZ(rawPackage);
        assertCurrent();
        outputURL = URL.createObjectURL(optimized); preparedState = selectedState;
        open.href = outputURL; open.hidden = !supportsAR;
        prepare.hidden = true; status.textContent = copy.ready;
        root.dispatchEvent(new CustomEvent('scdwb:ar-ready', {detail: {...result.report, state: JSON.parse(selectedState), bytes: optimized.size, originalBytes: rawPackage.byteLength}}));
        if (supportsAR && wantsAR && dialog.open) { status.textContent = copy.opening; open.click(); }
      } catch (error) {
        if (request !== revision || error.name === 'AbortError') return;
        release(); status.textContent = copy.error; prepare.textContent = copy.retry; prepare.hidden = false;
        console.error('Wheel AR preview:', error);
      } finally {
        if (request === revision) { busy = false; controller = null; prepare.disabled = !isReady(); dialog.removeAttribute('aria-busy'); }
      }
    }
    function updateLaunch() {
      const supported = mode === 'quick-look' || (mode === 'webxr' && viewer.canActivateAR);
      entry.hidden = !supported; launch.hidden = !supported;
      launch.disabled = mode === 'webxr' && !isReady();
    }
    async function detectDevice() {
      if (!mobile) return;
      if (supportsAR) mode = 'quick-look';
      else if (navigator.xr?.isSessionSupported) {
        try { if (await navigator.xr.isSessionSupported('immersive-ar')) mode = 'webxr'; } catch {}
      }
      if (mode === 'webxr') {
        viewer.setAttribute('ar', ''); viewer.setAttribute('ar-modes', 'webxr'); viewer.setAttribute('ar-scale', 'auto');
        const nativeButton = document.createElement('span'); nativeButton.slot = 'ar-button'; nativeButton.hidden = true; viewer.appendChild(nativeButton);
        setTimeout(updateLaunch, 1000);
      }
      label.textContent = copy.mobile_label;
      help.textContent = copy.unsupported; help.hidden = Boolean(mode); updateLaunch();
    }
    function openAndroid() {
      if (!isReady() || !viewer.canActivateAR) return;
      root.querySelector('[data-scdwb-view="3d"]')?.click();
      viewer.activateAR().catch(error => {
        if (!dialog.open) dialog.showModal(); status.textContent = copy.android_error;
        prepare.hidden = false; prepare.disabled = false; prepare.textContent = copy.retry;
        console.error('Wheel browser AR:', error);
      });
    }
    detectDevice();
    launch.addEventListener('click', () => {
      if (mode === 'webxr') { openAndroid(); return; }
      if (preparedState && preparedState !== stateKey()) invalidate();
      wantsAR = true;
      if (supportsAR && preparedState && isReady()) { open.click(); return; }
      dialog.showModal();
      status.textContent = supportsAR ? copy.loading : '';
      if (supportsAR && isReady()) generate();
    });
    dialog.querySelector('[data-scp-ar-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => { wantsAR = false; });
    dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    prepare.addEventListener('click', () => { if (mode === 'webxr') openAndroid(); else generate(); });
    open.addEventListener('click', event => { if (preparedState !== stateKey() || !isReady()) { event.preventDefault(); invalidate(); } });
    root.addEventListener('scdwb:selection', () => { invalidate(); updateLaunch(); });
    root.addEventListener('scdwb:preview-ready', () => {
      updateLaunch();
      if (!busy && !preparedState && supportsAR) {
        prepare.disabled = !isReady(); status.textContent = isReady() ? copy.intro : copy.loading;
        if (wantsAR && dialog.open && isReady()) generate();
      }
    });
    viewer.addEventListener('error', () => { invalidate(); updateLaunch(); });
    viewer.addEventListener('load', updateLaunch);
    viewer.addEventListener('ar-status', event => {
      if (mode === 'webxr' && event.detail.status === 'failed') {
        updateLaunch();
        if (!dialog.open) dialog.showModal(); status.textContent = copy.android_error;
        prepare.hidden = false; prepare.disabled = false; prepare.textContent = copy.retry;
      }
    });
    document.addEventListener('shopify:section:unload', event => { if (event.target.contains(root)) { ++revision; controller?.abort(); release(); } });
    window.addEventListener('pagehide', () => { ++revision; controller?.abort(); release(); });
    window.addEventListener('pageshow', event => { if (event.persisted) invalidate(); });
  }
  const start = () => customElements.whenDefined('model-viewer').then(() => document.querySelectorAll('scd-wheel-builder').forEach(initialize));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true}); else start();
  document.addEventListener('shopify:section:load', start);
})();
